import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  ShieldCheck,
  Clock,
  Wrench,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { SERVICES, STATS, SECTORS } from "@/data/services";
import CaseStudies from "@/components/CaseStudies";
import QRNameplate from "@/components/QRNameplate";
import Accreditations from "@/components/Accreditations";
import Testimonials from "@/components/Testimonials";
import Gallery from "@/components/Gallery";
import SectionDivider from "@/components/SectionDivider";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import Seo from "@/components/Seo";
import { trackPhoneClick, trackPortalClick } from "@/lib/track";

const PORTAL_URL = "https://portal.lutrom.co.uk";

const Hero = () => (
  <section
    className="relative overflow-hidden"
    data-testid="hero-section"
  >
    <PhotoBackdrop src="/gallery/db-panel-3.jpg" position="center 40%" />
    <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-20 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 relative">
      <div className="lg:col-span-7 fade-up">
        <p className="eyebrow" data-testid="hero-eyebrow">Enhancing reliability and performance</p>
        <h1
          className="font-display mt-6 text-white"
          style={{
            fontSize: "clamp(48px, 7.4vw, 96px)",
            lineHeight: 0.92,
          }}
          data-testid="hero-heading"
        >
          24/7 ON-HAND
          <br />
          ENGINEERS IN
          <br />
          <span className="gradient-blue-text">NORTH WEST ENGLAND</span>
        </h1>
        <p className="mt-7 text-[17px] leading-relaxed text-[color:var(--lt-text-dim)] max-w-2xl">
          Lutrom Engineering delivers class-leading mechanical and electrical
          solutions to industrial and commercial clients — including hydraulics,
          pneumatics, water systems, automation, welding, fabrication, fire alarms,
          emergency lighting and bespoke machinery. Depend on us, day or night.
        </p>
        <div className="mt-9 flex flex-wrap items-center gap-3">
          <Link
            to="/contact"
            className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
            data-testid="hero-cta-primary"
          >
            Request a Call Back <ArrowRight size={16} />
          </Link>
          <a
            href="tel:+447599474730"
            onClick={() => trackPhoneClick("hero")}
            className="btn-ghost inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
            data-testid="hero-cta-call"
          >
            <Phone size={16} /> +44 7599 474730
          </a>
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs uppercase tracking-widest text-[color:var(--lt-text-mute)]">
          <span className="inline-flex items-center gap-2"><ShieldCheck size={14} /> ISO 9001 / 14001 / 45001</span>
          <span className="inline-flex items-center gap-2"><ShieldCheck size={14} /> SafeContractor</span>
          <span className="inline-flex items-center gap-2"><ShieldCheck size={14} /> IPAF · City &amp; Guilds</span>
          <span className="inline-flex items-center gap-2"><Clock size={14} /> 24/7 Call-Out</span>
        </div>
      </div>

      <div className="lg:col-span-5 fade-up delay-2">
        <div className="glow-card rounded-2xl p-7 relative overflow-hidden">
          <div className="flex items-start gap-4">
            <div className="icon-tile">
              <Sparkles size={22} />
            </div>
            <div>
              <p className="eyebrow" style={{ color: "rgba(230,236,255,0.9)" }}>Operators</p>
              <h3
                className="font-display mt-2 text-white"
                style={{ fontSize: 28, letterSpacing: "0.02em" }}
              >
                NEED ENGINEERING SUPPORT?
              </h3>
            </div>
          </div>
          <p className="mt-5 text-sm text-[color:rgba(230,236,255,0.78)] leading-relaxed">
            Existing client? Track work orders, view reports and submit work requests
            via the Lutrom Operations Portal.
          </p>
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackPortalClick("hero")}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 font-semibold text-sm"
            style={{
              background: "rgba(6, 11, 24, 0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "white",
            }}
            data-testid="hero-portal-link"
          >
            Open Operations Portal <ArrowRight size={16} />
          </a>
          <div className="divider-line my-6 opacity-60" />
          <div className="grid grid-cols-3 gap-3 text-center">
            {[
              { label: "Admin", desc: "Ops & scheduling" },
              { label: "Engineers", desc: "Jobs & time" },
              { label: "Customers", desc: "Requests & reports" },
            ].map((b) => (
              <div
                key={b.label}
                className="rounded-lg p-3"
                style={{ background: "rgba(6, 11, 24, 0.45)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <p className="text-xs font-semibold text-white">{b.label}</p>
                <p className="text-[11px] text-[color:rgba(230,236,255,0.7)] mt-1">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="relative" data-testid="stats-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <div
        className="grid grid-cols-2 lg:grid-cols-3 rounded-2xl glass-card overflow-hidden"
      >
        {STATS.map((s, i) => (
          <div
            key={s.label || s.subtitle || i}
            className="px-6 py-7 lg:px-8 lg:py-10"
            style={{
              borderRight: i < STATS.length - 1 ? "1px solid var(--lt-border)" : "none",
              borderBottom: "1px solid var(--lt-border)",
            }}
            data-testid={`stat-${i}`}
          >
            {s.display ? (
              <>
                <p
                  className="font-display gradient-blue-text"
                  style={{ fontSize: "clamp(28px, 3.4vw, 44px)", lineHeight: 1, letterSpacing: "0.01em" }}
                >
                  {s.display.toUpperCase()}
                </p>
                <p className="mt-3 text-xs uppercase tracking-widest text-[color:var(--lt-text-dim)]">
                  {s.subtitle}
                </p>
              </>
            ) : (
              <>
                <p
                  className="font-display gradient-blue-text"
                  style={{ fontSize: 48, lineHeight: 1 }}
                >
                  {s.value}
                </p>
                <p className="mt-3 text-xs uppercase tracking-widest text-[color:var(--lt-text-dim)]">
                  {s.label}
                </p>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section className="relative overflow-hidden mt-24 lg:mt-32 py-20 lg:py-24" data-testid="about-section">
    <PhotoBackdrop src="/gallery/lathe.jpg" position="center 35%" dim={1.08} />
    <div className="max-w-7xl mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 relative">
      <div className="lg:col-span-5">
        <p className="eyebrow">About Lutrom</p>
        <h2
          className="font-display mt-5 text-white"
          style={{ fontSize: "clamp(36px, 5vw, 60px)", lineHeight: 0.95 }}
        >
          EXPERIENCED AND
          <br />
          <span className="gradient-blue-text">RELIABLE ENGINEERS.</span>
        </h2>
      </div>
      <div className="lg:col-span-7 space-y-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
        <p>
          Founded in 2021, Lutrom Engineering Ltd delivers mechanical, electrical
          and controls engineering to industrial and commercial clients — from
          factories and process plants to football stadiums, schools, retail stores
          and beyond. Innovation meets expertise — we deliver reliable, cutting-edge
          solutions where they matter most.
        </p>
        <p>
          From hydraulic and pneumatic systems to water and process pipework,
          automation, machine refurbishment, welding and bespoke machinery — we
          forge long-lasting partnerships built on trust, collaboration and mutual
          success. Our team works closely with clients to deliver bespoke,
          cost-effective solutions that drive business growth.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
          {[
            "Mechanical & electrical engineering",
            "Hydraulics, pneumatics & water systems",
            "Automation, PLC & HMI programming",
            "Welding & fabrication — steel, stainless, aluminium",
            "Bespoke machinery design & commissioning",
            "Machine refurbishment & upgrades",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3 text-sm text-white">
              <CheckCircle2
                size={18}
                className="mt-0.5 flex-shrink-0"
                color="var(--lt-blue-bright)"
              />
              <span>{t}</span>
            </div>
          ))}
        </div>
        <div className="pt-4">
          <Link
            to="/about"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white border-b border-[color:var(--lt-border-strong)] pb-0.5 hover:text-[color:var(--lt-blue-soft)] transition-colors"
            data-testid="about-learn-more"
          >
            More about Lutrom <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const Services = () => (
  <section className="relative mt-24 lg:mt-32" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div className="max-w-2xl">
          <p className="eyebrow">Our Engineering Services</p>
          <h2
            className="font-display mt-5 text-white"
            style={{ fontSize: "clamp(36px, 5vw, 60px)", lineHeight: 0.95 }}
          >
            A FULL SUITE OF
            <br />
            <span className="gradient-blue-text">ENGINEERING DISCIPLINES.</span>
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
            Mechanical, electrical, hydraulic, pneumatic and water systems —
            plus automation, welding, fabrication, fire alarms, emergency
            lighting, refurbishment and bespoke machinery. All under one roof.
          </p>
        </div>
        <Link
          to="/services"
          className="btn-ghost inline-flex w-fit items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold"
          data-testid="services-view-all"
        >
          View all services <ArrowRight size={15} />
        </Link>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <Link
              to={`/services#${s.slug}`}
              key={s.slug}
              className="service-card rounded-xl p-7 block fade-up"
              style={{ animationDelay: `${80 * i}ms` }}
              data-testid={`service-card-${s.slug}`}
            >
              <div className="icon-tile">
                <Icon size={22} />
              </div>
              <h3
                className="font-display mt-5 text-white"
                style={{ fontSize: 22, letterSpacing: "0.02em" }}
              >
                {s.title.toUpperCase()}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[color:var(--lt-text-dim)]">
                {s.short}
              </p>
              <p className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--lt-blue-soft)]">
                Learn more <ArrowRight size={13} />
              </p>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

const Sectors = () => (
  <section className="relative overflow-hidden mt-24 lg:mt-32 py-16 lg:py-20" data-testid="sectors-section">
    <PhotoBackdrop src="/gallery/taping-cage.jpg" position="center 50%" dim={1.05} />
    <div className="max-w-7xl mx-auto px-5 lg:px-10 relative">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Industries we serve</p>
          <h2
            className="font-display mt-4 text-white"
            style={{ fontSize: "clamp(30px, 3.6vw, 44px)", lineHeight: 1 }}
          >
            TRUSTED ACROSS INDUSTRIES.
          </h2>
        </div>
      </div>
      <div className="mt-10 overflow-hidden">
        <div className="flex flex-wrap gap-3">
          {SECTORS.map((s, i) => (
            <span
              key={`${s}-${i}`}
              className="px-4 py-2 rounded-full text-xs uppercase tracking-widest font-semibold"
              style={{
                color: "var(--lt-text)",
                background: "rgba(15, 26, 51, 0.6)",
                border: "1px solid var(--lt-border)",
              }}
              data-testid={`sector-pill-${i}`}
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="relative overflow-hidden mt-24 lg:mt-32 py-16 lg:py-20" data-testid="process-section">
    <PhotoBackdrop src="/gallery/refurb-stand.jpg" position="center 40%" dim={1.05} />
    <div className="max-w-7xl mx-auto px-5 lg:px-10 relative">
      <p className="eyebrow">How we work</p>
      <h2
        className="font-display mt-4 text-white"
        style={{ fontSize: "clamp(32px, 4vw, 52px)", lineHeight: 0.95 }}
      >
        A SIMPLE,
        <span className="gradient-blue-text"> PRECISE PROCESS.</span>
      </h2>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-5">
        {[
          { n: "01", t: "Site Survey", d: "Walk-through, asset register and risk review." },
          { n: "02", t: "Bespoke Plan", d: "Custom PPM, install or report scope aligned to your goals." },
          { n: "03", t: "Execute", d: "Skilled engineers deliver on-site with full documentation." },
          { n: "04", t: "Handover", d: "Clear reports, action items and ongoing support." },
        ].map((s) => (
          <div key={s.n} className="glass-card rounded-xl p-6" data-testid={`process-step-${s.n}`}>
            <p
              className="font-display gradient-blue-text"
              style={{ fontSize: 36, lineHeight: 1 }}
            >
              {s.n}
            </p>
            <p className="mt-4 font-display text-white" style={{ fontSize: 20 }}>
              {s.t.toUpperCase()}
            </p>
            <p className="mt-3 text-sm text-[color:var(--lt-text-dim)] leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section className="relative mt-24 lg:mt-32" data-testid="cta-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <div
        className="rounded-2xl p-10 lg:p-16 relative overflow-hidden glow-card"
      >
        <PhotoBackdrop src="/gallery/commissioning-cage.jpg" position="center 45%" dim={1.05} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end relative">
          <div className="lg:col-span-8">
            <p className="eyebrow">Ready when you are</p>
            <h2
              className="font-display mt-4 text-white"
              style={{ fontSize: "clamp(34px, 4.6vw, 60px)", lineHeight: 0.95 }}
            >
              GOT A BREAKDOWN, A PROJECT,
              <br />
              OR A QUESTION? <span className="gradient-blue-text">CALL LUTROM.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <a
              href="tel:+447599474730"
              onClick={() => trackPhoneClick("bottom_cta")}
              className="btn-primary inline-flex items-center justify-center gap-2 rounded-md px-5 py-3.5 text-sm font-semibold"
              data-testid="cta-call"
            >
              <Phone size={16} /> Call +44 7599 474730
            </a>
            <Link
              to="/contact"
              className="btn-ghost inline-flex items-center justify-center gap-2 rounded-md px-5 py-3.5 text-sm font-semibold"
              data-testid="cta-contact"
            >
              <Wrench size={16} /> Send an Enquiry
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => {
  return (
    <div data-testid="home-page">
      <Seo
        title="24/7 Mechanical & Electrical Engineers"
        description="Lutrom Engineering — 24/7 mechanical, electrical, hydraulic, pneumatic, water systems, automation, welding, fire alarms and bespoke machinery services across North West England."
        path="/"
      />
      <Hero />
      <Stats />
      <SectionDivider label="ABOUT" />
      <About />
      <SectionDivider label="SERVICES" />
      <Services />
      <SectionDivider label="FEATURED PROJECT" />
      <CaseStudies />
      <SectionDivider label="SMART NAMEPLATE" />
      <QRNameplate />
      <SectionDivider label="ACCREDITATIONS" />
      <Accreditations />
      <SectionDivider label="TESTIMONIALS" />
      <Testimonials />
      <SectionDivider label="ON THE TOOLS" />
      <Gallery />
      <SectionDivider label="INDUSTRIES" />
      <Sectors />
      <SectionDivider label="HOW WE WORK" />
      <Process />
      <CTA />
    </div>
  );
};

export default Home;
